import { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { trackingApi } from '../api/tracking';
import { ReadOnlyBanner } from './ReadOnlyBanner';

export interface RecordHeaderField {
  label: string;
  value: ReactNode;
}

interface RecordHeaderProps {
  title: string;
  fields: RecordHeaderField[];
  actions?: ReactNode;
  readOnly?: boolean;
  /** Pass 'mine' for the My Profile self-service case, matching AuditHistoryTab's own convention. */
  entityType: string;
  entityId?: number;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * Graphical two-column header matching the OneGMS reference: a colored
 * title bar, a label/value grid, and a footer showing who created and
 * who last touched the record. Created-by/updated-by are real, not
 * placeholders - the oldest and newest entries in the same audit
 * history AuditHistoryTab already reads, so this needs no new backend
 * endpoint and no per-entity config.
 */
export function RecordHeader({ title, fields, actions, readOnly, entityType, entityId }: RecordHeaderProps) {
  const isMine = entityType === 'mine';
  const historyQuery = useQuery({
    queryKey: ['tracking', entityType, entityId],
    queryFn: () => (isMine ? trackingApi.mine() : trackingApi.forEntity(entityType, entityId as number)),
    enabled: isMine || entityId !== undefined,
  });

  const entries = historyQuery.data?.data ?? [];
  // history() returns newest-first (DESC) - see audit.engine.ts.
  const createdEntry = entries.length > 0 ? entries[entries.length - 1] : null;
  const updatedEntry = entries.length > 0 ? entries[0] : null;

  return (
    <div className="record-header">
      <div className="record-header-bar">
        <span className="record-header-title">{title}</span>
        {actions && <div className="record-header-actions">{actions}</div>}
      </div>
      <div className="record-header-grid">
        {fields.map((f) => (
          <div key={f.label} className="record-header-field">
            <div className="record-header-field-label">{f.label}</div>
            <div className="record-header-field-value">{f.value}</div>
          </div>
        ))}
      </div>
      {(createdEntry || updatedEntry) && (
        <div className="record-header-footer">
          <span>{createdEntry ? <>Created by <strong>{createdEntry.actor_name ?? 'System'}</strong> · {formatDate(createdEntry.created_at)}</> : null}</span>
          <span>{updatedEntry && updatedEntry !== createdEntry ? <>Last updated by <strong>{updatedEntry.actor_name ?? 'System'}</strong> · {formatDate(updatedEntry.created_at)}</> : null}</span>
        </div>
      )}
      {readOnly && <ReadOnlyBanner />}
    </div>
  );
}
